import React, { memo, useMemo, useState, useRef, useCallback, useEffect } from 'react';
import GanttBar from './GanttBar';
import { getBarColor } from '../utils';
import { formatDurationRange } from '../../../../utils/taskDuration';
import { getTaskEstimatedDurationRange } from '../../FlowchartView/utils/progressUtils';

/**
 * TaskRow Component
 * 
 * Renders a single task row with its name and timeline bar.
 * Includes today marker in the timeline area.
 * Supports inline editing of task names.
 * 
 * Memoized to prevent re-renders during zoom animation.
 */
const TaskRow = memo(({
  task,
  taskDates,
  isLocked = false,
  lockReason = '',
  viewStartDate,
  totalDays,
  statuses,
  allTags = [],
  colorBy = 'status',
  todayPosition,
  cursorPosition,
  onDateChange,
  onDueDateChange,
  timelineMinWidth = 800,
  isSelected = false,
  onSelect,
  onToggleSelect,
  selectedTaskIds,
  onMultiDrag,
  multiDragDelta = 0,
  isDraggingMultiple = false,
  multiDragType = null,
  zoomLevel = 'day',
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  onRenameTask,
  nestedSprints = [],
  allSprints = [],
}) => {
  // Get bar color based on colorBy setting
  const barColor = useMemo(() => {
    return getBarColor(task, colorBy, statuses, allTags);
  }, [task, colorBy, statuses, allTags]);

  const durationLabel = useMemo(() => {
    const range = getTaskEstimatedDurationRange(task, { statuses, nestedSprints, allSprints });
    return range.maxMinutes != null
      ? formatDurationRange(range.minMinutes ?? 0, range.maxMinutes)
      : '';
  }, [task, statuses, nestedSprints, allSprints]);

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(task.name || '');
  const inputRef = useRef(null);
  const autoStartedForTaskRef = useRef(null);

  // Determine if this is a placeholder name (newly created task)
  const taskName = task.name || '';
  const isDecision = task?.nodeType === 'decision';
  const isPlaceholderName = !taskName || taskName === 'ENTER MISSION NAME' || taskName === 'New Decision';
  const placeholderHint = isDecision ? 'Type a decision question...' : 'Type a mission name...';

  // Start editing handler
  const handleStartEdit = useCallback((e) => {
    e.stopPropagation();
    if (isPlaceholderName) {
      setEditValue('');
    } else {
      setEditValue(taskName);
    }
    setIsEditing(true);
  }, [taskName, isPlaceholderName]);

  // Finish editing handler
  const handleFinishEdit = useCallback(() => {
    const trimmedValue = editValue.trim();
    if (trimmedValue !== taskName) {
      onRenameTask?.(task.id, trimmedValue);
    }
    setIsEditing(false);
  }, [editValue, task.id, taskName, onRenameTask]);

  // Keyboard handler
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFinishEdit();
    } else if (e.key === 'Escape') {
      setEditValue(taskName);
      setIsEditing(false);
    }
  }, [handleFinishEdit, taskName]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Auto-start editing when task has placeholder name (newly created)
  useEffect(() => {
    if (isPlaceholderName && !isEditing && autoStartedForTaskRef.current !== task.id) {
      autoStartedForTaskRef.current = task.id;
      setEditValue('');
      setIsEditing(true);
    }
  }, [task.id, isPlaceholderName, isEditing]);

  // Listen for ganttStartEditing event (triggered by focusMission for new tasks)
  useEffect(() => {
    const handleStartEditing = (e) => {
      if (e.detail?.taskId === task.id) {
        setEditValue(isPlaceholderName ? '' : taskName);
        setIsEditing(true);
      }
    };
    
    window.addEventListener('ganttStartEditing', handleStartEditing);
    return () => window.removeEventListener('ganttStartEditing', handleStartEditing);
  }, [task.id, taskName, isPlaceholderName]);

  const rowClasses = [
    'gantt-row',
    isLocked && 'gantt-row--locked',
    isEditing && 'gantt-row--editing',
  ].filter(Boolean).join(' ');

  return (
    <div className={rowClasses} data-gantt-task-id={task.id}>
      {/* Task name (left sidebar) - clickable to edit */}
      <div className="gantt-row__sidebar">
        <div className="gantt-row__name-row">
          {isEditing ? (
            <div className="gantt-row__edit-container">
              {editValue === '' && (
                <span className="gantt-row__name-hint">💡 {placeholderHint}</span>
              )}
              <input
                ref={inputRef}
                type="text"
                className="gantt-row__input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleFinishEdit}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>
          ) : (
            <span 
              className={`gantt-row__name ${isPlaceholderName ? 'gantt-row__name--placeholder' : ''}`}
              onClick={handleStartEdit}
              onMouseDown={(e) => e.stopPropagation()}
              title={onRenameTask ? 'Click to edit name' : undefined}
            >
              {taskName || (isDecision ? 'Untitled decision' : 'Untitled mission')}
            </span>
          )}
          {/* Lock indicator */}
          {isLocked && !isEditing && (
            <div 
              className="gantt-row__lock-indicator"
              title={lockReason || 'This mission is locked until conditions are met'}
            >
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="gantt-row__lock-icon"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          )}
        </div>
        <span className="gantt-row__duration" title={durationLabel || undefined}>
          {durationLabel}
        </span>
      </div>

      {/* Timeline area with bar */}
      <div className="gantt-row__timeline" style={{ minWidth: timelineMinWidth }}>
        {/* Today marker line */}
        {todayPosition !== null && (
          <div 
            className="gantt-today-marker"
            style={{ left: `${todayPosition}%` }}
          />
        )}

        {/* Cursor/playhead marker (DAW-style) */}
        {cursorPosition !== null && (
          <div 
            className="gantt-cursor-marker"
            style={{ left: `${cursorPosition}%` }}
          />
        )}

        <GanttBar
          task={task}
          taskId={task.id}
          taskName={task.name}
          startDate={taskDates.startDate}
          endDate={taskDates.endDate}
          dueDate={taskDates.dueDate}
          hasEstimate={taskDates.hasEstimate}
          viewStartDate={viewStartDate}
          totalDays={totalDays}
          statusColor={barColor}
          isLocked={isLocked}
          onDateChange={onDateChange}
          onDueDateChange={onDueDateChange}
          isSelected={isSelected}
          onSelect={onSelect}
          onToggleSelect={onToggleSelect}
          selectedTaskIds={selectedTaskIds}
          onMultiDrag={onMultiDrag}
          multiDragDelta={multiDragDelta}
          isDraggingMultiple={isDraggingMultiple}
          multiDragType={multiDragType}
          zoomLevel={zoomLevel}
          onDecisionAnswerChange={onDecisionAnswerChange}
          onDecisionOptionsChange={onDecisionOptionsChange}
        />
      </div>
    </div>
  );
});

TaskRow.displayName = 'TaskRow';

export default TaskRow;
